import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Assertion value style helper
const getAssertionStyle = (value) => {
  if (value === true) return 'bg-emerald-500/20 text-emerald-400';
  if (value === false) return 'bg-red-500/20 text-red-400';
  return 'bg-zinc-700 text-zinc-300';
};

// Status indicator component
const StatusBadge = ({ status, isMock }) => {
  const isOk = status === 'ok';
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded-full ${isOk ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
      <span className={`font-mono text-sm ${isOk ? 'text-emerald-400' : 'text-red-400'}`}>
        {status?.toUpperCase() || 'UNKNOWN'}
      </span>
      {isMock && (
        <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded border border-amber-500/30">
          MOCK
        </span>
      )}
    </div>
  );
};

// Verification badge
const VerificationBadge = ({ result }) => {
  const isSuccess = result === 'SUCCESS';
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${
      isSuccess 
        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
        : 'bg-red-500/10 border-red-500/30 text-red-400'
    }`}>
      <span className="text-lg">{isSuccess ? '✓' : '✗'}</span>
      <span className="font-bold tracking-wide">{result}</span>
    </div>
  );
};

// Hash display with truncation
const HashDisplay = ({ label, hash, className = '' }) => {
  if (!hash) return null;
  const truncated = hash.length > 40 ? `${hash.slice(0, 20)}...${hash.slice(-12)}` : hash;
  return (
    <div className={`font-mono text-xs ${className}`}>
      <span className="text-zinc-500">{label}: </span>
      <span className="text-zinc-300" title={hash}>{truncated}</span>
    </div>
  );
};

export default function CARProof() {
  const [nodeStatus, setNodeStatus] = useState(null);
  const [signedReceipt, setSignedReceipt] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [loading, setLoading] = useState({ status: false, sign: false, verify: false });
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1=status, 2=sign, 3=verify

  const fetchStatus = useCallback(async () => {
    setLoading(l => ({ ...l, status: true }));
    setError(null);
    try {
      const res = await axios.get(`${API}/katlas/status`);
      setNodeStatus(res.data);
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to connect to KATLASNET');
      setNodeStatus(null);
    } finally {
      setLoading(l => ({ ...l, status: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // API and axios are module constants

  // Fetch node status on mount
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const signReceipt = async () => {
    setLoading(l => ({ ...l, sign: true }));
    setError(null);
    setVerifyResult(null);
    try {
      const res = await axios.post(`${API}/katlas/sign`, {
        receipt_type: "handover_attestation",
        actor_role: "TerminalOperator",
        counterparty_role: "Carrier",
        assertions: {
          seal_id: "S-8891",
          seal_intact: true,
          damage_visible: false,
          temp_range_ok: true
        }
      });
      setSignedReceipt(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to sign receipt');
    } finally {
      setLoading(l => ({ ...l, sign: false }));
    }
  };

  const verifyReceipt = async () => {
    if (!signedReceipt?.signed_receipt) return;
    setLoading(l => ({ ...l, verify: true }));
    setError(null);
    try {
      const res = await axios.post(`${API}/katlas/verify`, {
        signed_receipt: signedReceipt.signed_receipt
      });
      setVerifyResult(res.data);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to verify receipt');
    } finally {
      setLoading(l => ({ ...l, verify: false }));
    }
  };

  const resetFlow = () => {
    setSignedReceipt(null);
    setVerifyResult(null);
    setStep(1);
    fetchStatus();
  };

  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Navigation back to demos */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="text-zinc-500 hover:text-zinc-300 text-sm flex items-center gap-2"
          >
            ← Back to Demos
          </button>
          <span className="px-3 py-1 bg-zinc-800 text-zinc-500 text-xs rounded-full">
            Technical Dashboard
          </span>
        </div>

        {/* Header */}
        <header className="border-b border-zinc-800 pb-4">
          <h1 className="text-2xl font-bold tracking-tight text-white">
            CAR Proof Dashboard
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Custody · Authority · Receipts — KATLASNET Integration
          </p>
        </header>

        {/* Step Progress */}
        <div className="flex items-center gap-4 text-sm" data-testid="step-progress">
          {[
            { n: 1, label: 'Status' },
            { n: 2, label: 'Sign' },
            { n: 3, label: 'Verify' }
          ].map(({ n, label }) => (
            <div key={n} className={`flex items-center gap-2 ${step >= n ? 'text-emerald-400' : 'text-zinc-600'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${
                step >= n ? 'border-emerald-400 bg-emerald-400/10' : 'border-zinc-700'
              }`}>{n}</span>
              <span>{label}</span>
              {n < 3 && <span className="text-zinc-700 ml-2">→</span>}
            </div>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400" data-testid="error-alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* STEP 1: Status Card */}
        <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6" data-testid="status-card">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-zinc-200 mb-3">KATLASNET Node Status</h2>
              {nodeStatus ? (
                <div className="space-y-2">
                  <StatusBadge status={nodeStatus.status} isMock={nodeStatus.mock_mode} />
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mt-3">
                    <div><span className="text-zinc-500">Service:</span> <span className="text-zinc-300">{nodeStatus.service}</span></div>
                    <div><span className="text-zinc-500">Version:</span> <span className="text-zinc-300 font-mono">{nodeStatus.version}</span></div>
                    <div><span className="text-zinc-500">Node ID:</span> <span className="text-zinc-300 font-mono">{nodeStatus.node_id}</span></div>
                    <div><span className="text-zinc-500">Time:</span> <span className="text-zinc-300">{nodeStatus.time_utc}</span></div>
                  </div>
                </div>
              ) : (
                <p className="text-zinc-500">{loading.status ? 'Connecting...' : 'Not connected'}</p>
              )}
            </div>
            <button
              onClick={fetchStatus}
              disabled={loading.status}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm transition-colors disabled:opacity-50"
              data-testid="refresh-status-btn"
            >
              {loading.status ? 'Checking...' : 'Refresh'}
            </button>
          </div>
        </section>

        {/* STEP 2: Create Attestation */}
        <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6" data-testid="attestation-section">
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Create Attestation</h2>
          
          {!signedReceipt ? (
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">
                Generate a handover attestation with cryptographic proof of custody transfer.
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-sm">
                <div className="grid grid-cols-2 gap-2 text-zinc-400">
                  <div>Type: <span className="text-zinc-200">Handover Attestation</span></div>
                  <div>Actor: <span className="text-zinc-200">Terminal Operator</span></div>
                  <div>Counterparty: <span className="text-zinc-200">Carrier</span></div>
                  <div>Policy: <span className="text-zinc-200 font-mono">COU-FREIGHT-001</span></div>
                </div>
              </div>
              <button
                onClick={signReceipt}
                disabled={loading.sign || !nodeStatus || nodeStatus.status !== 'ok'}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors"
                data-testid="sign-receipt-btn"
              >
                {loading.sign ? 'Signing...' : 'Sign Receipt'}
              </button>
            </div>
          ) : (
            /* PROOF VIEW */
            <div className="space-y-4" data-testid="proof-view">
              <VerificationBadge result={signedReceipt.verification} />
              
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                {/* Fingerprint & Hash */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-400 font-mono text-sm">
                    Signer: {signedReceipt.signer_fingerprint}
                  </div>
                  <HashDisplay label="Receipt Hash" hash={signedReceipt.signed_receipt?.receipt_hash} />
                </div>
                
                {/* Plain English Summary */}
                <div className="border-t border-zinc-700 pt-3 mt-3">
                  <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Attestation Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-zinc-500">Who:</span>{' '}
                      <span className="text-zinc-200">{signedReceipt.signed_receipt?.payload?.actor_role} → {signedReceipt.signed_receipt?.payload?.counterparty_role}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">What:</span>{' '}
                      <span className="text-zinc-200">{signedReceipt.signed_receipt?.payload?.receipt_type?.replace(/_/g, ' ')}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">When:</span>{' '}
                      <span className="text-zinc-200">{signedReceipt.timestamp_utc}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Event ID:</span>{' '}
                      <span className="text-zinc-200 font-mono">{signedReceipt.signed_receipt?.payload?.event_id}</span>
                    </div>
                  </div>
                </div>

                {/* Assertions */}
                <div className="border-t border-zinc-700 pt-3">
                  <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Assertions</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(signedReceipt.signed_receipt?.payload?.assertions || {}).map(([k, v]) => (
                      <span key={k} className={`px-2 py-1 rounded text-xs font-mono ${getAssertionStyle(v)}`}>
                        {k}: {String(v)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Evidence Hashes */}
                {signedReceipt.signed_receipt?.payload?.evidence_hashes?.length > 0 && (
                  <div className="border-t border-zinc-700 pt-3">
                    <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Evidence</h4>
                    <div className="space-y-1">
                      {signedReceipt.signed_receipt.payload.evidence_hashes.map((h, i) => (
                        <HashDisplay key={`evidence-${h.slice(0,16)}`} label={`Evidence ${i + 1}`} hash={h} className="text-zinc-400" />
                      ))}
                    </div>
                  </div>
                )}

                {/* Signer Info */}
                <div className="border-t border-zinc-700 pt-3">
                  <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Signer</h4>
                  <div className="text-sm">
                    <div><span className="text-zinc-500">Issuer:</span> <span className="text-zinc-200">{signedReceipt.signed_receipt?.signer?.issuer}</span></div>
                    <div><span className="text-zinc-500">Key ID:</span> <span className="text-zinc-200 font-mono">{signedReceipt.signed_receipt?.signer?.key_id}</span></div>
                  </div>
                </div>

                {/* Certificate Text (collapsible) */}
                {signedReceipt.signed_receipt?.cert_text && (
                  <details className="border-t border-zinc-700 pt-3">
                    <summary className="text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer hover:text-zinc-300">
                      View Certificate
                    </summary>
                    <pre className="mt-2 p-3 bg-zinc-950 rounded text-xs text-emerald-400/80 font-mono overflow-x-auto whitespace-pre-wrap" data-testid="cert-text">
                      {signedReceipt.signed_receipt.cert_text}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}
        </section>

        {/* STEP 3: Verify */}
        {signedReceipt && (
          <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6" data-testid="verify-section">
            <h2 className="text-lg font-semibold text-zinc-200 mb-4">Verify Receipt</h2>
            
            {!verifyResult ? (
              <button
                onClick={verifyReceipt}
                disabled={loading.verify}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 rounded-lg font-medium transition-colors"
                data-testid="verify-receipt-btn"
              >
                {loading.verify ? 'Verifying...' : 'Verify Signed Receipt'}
              </button>
            ) : (
              <div className="space-y-4" data-testid="verify-result">
                <div className="flex items-center gap-4">
                  <VerificationBadge result={verifyResult.verification} />
                  {verifyResult.mock_mode && (
                    <span className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded border border-amber-500/30">
                      MOCK VERIFICATION
                    </span>
                  )}
                </div>
                
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2 text-sm">
                  <HashDisplay label="Receipt Hash" hash={verifyResult.receipt_hash} className="text-emerald-400" />
                  <div>
                    <span className="text-zinc-500">Signer Fingerprint:</span>{' '}
                    <span className="text-emerald-400 font-mono">{verifyResult.signer_fingerprint}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Verified At:</span>{' '}
                    <span className="text-zinc-300">{verifyResult.timestamp_utc}</span>
                  </div>
                </div>

                <button
                  onClick={resetFlow}
                  className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm transition-colors"
                  data-testid="reset-flow-btn"
                >
                  Start New Attestation
                </button>
              </div>
            )}
          </section>
        )}

        {/* Footer */}
        <footer className="text-center text-zinc-600 text-xs pt-4 border-t border-zinc-800">
          KATLASNET CAR Layer Integration · No KATLAS code embedded · External service only
        </footer>
      </div>
    </div>
  );
}
